import { useCallback, useRef, useState } from "react";

type RecordingResult = {
  blob: Blob;
  base64: string;
  durationMs: number;
  mimeType: string;
};

const TIMESLICE_MS = 250;

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result === "string") {
        const base64 = result.split(",")[1] ?? "";
        resolve(base64);
        return;
      }
      reject(new Error("Failed to read recording"));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function getAudioOnlyStream(stream: MediaStream): MediaStream | null {
  const audioTracks = stream.getAudioTracks();
  if (audioTracks.length === 0) return null;
  return new MediaStream(audioTracks);
}

function pickRecorderMimeType(): string | undefined {
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "video/webm;codecs=opus",
  ];
  return candidates.find((type) => MediaRecorder.isTypeSupported(type));
}

export function useVoiceRecorder(stream: MediaStream | null) {
  const [isRecording, setIsRecording] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef<number>(0);
  const mimeTypeRef = useRef<string>("audio/webm");
  const isRecordingRef = useRef(false);

  const stopActiveRecorder = useCallback(async (): Promise<void> => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === "inactive") {
      recorderRef.current = null;
      isRecordingRef.current = false;
      setIsRecording(false);
      return;
    }

    await new Promise<void>((resolve) => {
      recorder.onstop = () => resolve();
      recorder.stop();
    });

    recorderRef.current = null;
    isRecordingRef.current = false;
    setIsRecording(false);
  }, []);

  const startRecording = useCallback(async () => {
    if (!stream || isRecordingRef.current) return;

    const audioStream = getAudioOnlyStream(stream);
    if (!audioStream) {
      console.error("[Intake] No audio track available on media stream");
      return;
    }

    await stopActiveRecorder();

    chunksRef.current = [];
    startedAtRef.current = Date.now();

    const mimeType = pickRecorderMimeType();
    mimeTypeRef.current = mimeType ?? "audio/webm";

    let recorder: MediaRecorder;
    try {
      recorder = mimeType
        ? new MediaRecorder(audioStream, { mimeType })
        : new MediaRecorder(audioStream);
    } catch (error) {
      console.error("[Intake] MediaRecorder init failed:", error);
      recorder = new MediaRecorder(audioStream);
    }

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };

    recorder.onerror = (event) => {
      console.error("[Intake] MediaRecorder error:", event);
    };

    recorder.start(TIMESLICE_MS);
    recorderRef.current = recorder;
    isRecordingRef.current = true;
    setIsRecording(true);

    console.info("[Intake] Recording started:", recorder.mimeType || mimeTypeRef.current);
  }, [stream, stopActiveRecorder]);

  const stopRecording = useCallback(async (): Promise<RecordingResult | null> => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === "inactive") {
      isRecordingRef.current = false;
      setIsRecording(false);
      console.warn("[Intake] stopRecording called but recorder was inactive");
      return null;
    }

    return new Promise((resolve) => {
      recorder.onstop = async () => {
        const durationMs = Date.now() - startedAtRef.current;
        const mimeType = mimeTypeRef.current || recorder.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: mimeType });
        recorderRef.current = null;
        chunksRef.current = [];
        isRecordingRef.current = false;
        setIsRecording(false);

        if (blob.size === 0) {
          console.warn("[Intake] Recording blob is empty — speak before tapping Next");
          resolve(null);
          return;
        }

        const base64 = await blobToBase64(blob);
        console.info(
          `[Intake] Recording stopped: ${Math.round(durationMs / 1000)}s, ${blob.size} bytes, ${mimeType}`
        );
        resolve({ blob, base64, durationMs, mimeType });
      };

      recorder.requestData();
      recorder.stop();
    });
  }, []);

  return { isRecording, startRecording, stopRecording };
}
